import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { eventsService } from '@/services/eventsService';
import {
  Event,
  EventType,
  EventsListFilters,
  CreateEventData,
  UpdateEventData,
} from '@/types/events';

/**
 * Query key factory for events
 */
export const eventsKeys = {
  all: ['events'] as const,
  lists: () => [...eventsKeys.all, 'list'] as const,
  list: (filters?: EventsListFilters) => [...eventsKeys.lists(), filters] as const,
  details: () => [...eventsKeys.all, 'detail'] as const,
  detail: (id: number) => [...eventsKeys.details(), id] as const,
  types: () => [...eventsKeys.all, 'types'] as const,
};

/**
 * Hook to fetch list of events
 */
export const useEvents = (
  filters?: EventsListFilters,
  options?: Omit<UseQueryOptions<Event[]>, 'queryKey' | 'queryFn'>
) => {
  const enabled = options?.enabled !== false && !!filters?.account_id;

  return useQuery<Event[]>({
    queryKey: eventsKeys.list(filters),
    queryFn: () => eventsService.getEvents(filters),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Hook to fetch a specific event
 */
export const useEvent = (
  id: number,
  options?: Omit<UseQueryOptions<Event>, 'queryKey' | 'queryFn'>
) => {
  const enabled = options?.enabled !== false && id > 0;

  return useQuery<Event>({
    queryKey: eventsKeys.detail(id),
    queryFn: () => eventsService.getEvent(id),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to fetch event types
 */
export const useEventTypes = (
  options?: Omit<UseQueryOptions<EventType[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<EventType[]>({
    queryKey: eventsKeys.types(),
    queryFn: () => eventsService.getEventTypes(),
    staleTime: 1000 * 60 * 60, // 1 hour (types don't change often)
    retry: 1,
    ...options,
  });
};

/**
 * Hook to create a new event
 */
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventData) => eventsService.createEvent(data),
    onSuccess: (newEvent) => {
      // Invalidate all events lists to refetch
      queryClient.invalidateQueries({
        queryKey: eventsKeys.lists(),
      });

      // Optionally, add the new event to the cache
      queryClient.setQueryData(eventsKeys.detail(newEvent.id), newEvent);
    },
    onError: (error) => {
      console.error('[useCreateEvent] Mutation error:', error);
    },
  });
};

/**
 * Hook to update an existing event
 */
export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateEventData }) =>
      eventsService.updateEvent(id, data),
    onSuccess: (updatedEvent, variables) => {
      // Update the detail cache
      queryClient.setQueryData(eventsKeys.detail(variables.id), updatedEvent);

      // Invalidate all lists to refetch
      queryClient.invalidateQueries({
        queryKey: eventsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('[useUpdateEvent] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete an event
 */
export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => eventsService.deleteEvent(id),
    onSuccess: (_, deletedId) => {
      // Remove from detail cache
      queryClient.removeQueries({
        queryKey: eventsKeys.detail(deletedId),
      });

      // Invalidate all lists to refetch
      queryClient.invalidateQueries({
        queryKey: eventsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('[useDeleteEvent] Mutation error:', error);
    },
  });
};
